// Distance + ordering helpers used by the route planner.
//
// Routing model: pure haversine ("as-the-crow-flies") distances. Fine for
// dispatch-grade planning where leg counts are 10–100 stops. Promote to a real
// road-network solver (Mapbox Optimization, OSRM, OR-Tools) only when door-to-
// door drive time matters more than picker latency.

const EARTH_RADIUS_M = 6_371_000;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Great-circle distance in meters between two lat/lng points.
 *  Internal-only — used by the planner/2-opt below. Not exported because no
 *  external consumer needs it directly; promote if/when that changes. */
function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

export interface MappablePoint {
  id: string;
  lat: number;
  lng: number;
}

export interface OrderedRoute<T extends MappablePoint> {
  ordered: T[];
  /** Per-leg distance in meters. legs[0] is start → ordered[0]; legs[i] for
   *  i>0 is ordered[i-1] → ordered[i]; if an `end` is provided, the final
   *  ordered[last] → end leg is appended. */
  legs: number[];
  totalMeters: number;
}

type LatLng = { lat: number; lng: number };

// 2-opt cap. For n=100 we converge in ~5 passes; 32 is paranoid headroom.
// Pathological geometries can oscillate within a floating-point epsilon, so
// the cap prevents an infinite loop even when the EPS guard misses a tie.
const TWO_OPT_MAX_PASSES = 32;
const TWO_OPT_EPS_M = 1e-6;

function greedyNearestNeighbor<T extends MappablePoint>(
  start: LatLng,
  points: T[],
): T[] {
  const remaining = points.slice();
  const ordered: T[] = [];
  let cursor: LatLng = start;
  while (remaining.length > 0) {
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const d = haversineMeters(cursor, remaining[i]);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    const next = remaining.splice(bestIdx, 1)[0];
    ordered.push(next);
    cursor = next;
  }
  return ordered;
}

/**
 * 2-opt path improvement. Given a tour `start → a[0] → … → a[n-1] → end?`,
 * repeatedly find a pair of edges whose removal-and-reconnection (i.e. a
 * sub-path reversal) shortens the tour, and apply it until no swap improves.
 *
 * Mutates a copy of `ordered` and returns it. Pure haversine — symmetric
 * distances mean reversing a segment leaves its internal length unchanged,
 * so only the two boundary edges affect the delta.
 */
function twoOptImprove<T extends MappablePoint>(
  start: LatLng,
  ordered: T[],
  end: LatLng | null,
): T[] {
  const n = ordered.length;
  if (n < 2) return ordered;
  const arr = ordered.slice();

  for (let pass = 0; pass < TWO_OPT_MAX_PASSES; pass++) {
    let improved = false;
    for (let i = 0; i < n - 1; i++) {
      for (let j = i + 1; j < n; j++) {
        const pred: LatLng = i === 0 ? start : arr[i - 1];
        const succ: LatLng | null = j === n - 1 ? end : arr[j + 1];

        const beforeLeft = haversineMeters(pred, arr[i]);
        const afterLeft = haversineMeters(pred, arr[j]);
        let delta = afterLeft - beforeLeft;
        if (succ) {
          const beforeRight = haversineMeters(arr[j], succ);
          const afterRight = haversineMeters(arr[i], succ);
          delta += afterRight - beforeRight;
        }

        if (delta < -TWO_OPT_EPS_M) {
          // Reverse arr[i..j] in place.
          let lo = i;
          let hi = j;
          while (lo < hi) {
            const tmp = arr[lo];
            arr[lo] = arr[hi];
            arr[hi] = tmp;
            lo++;
            hi--;
          }
          improved = true;
        }
      }
    }
    if (!improved) break;
  }
  return arr;
}

function buildLegs<T extends MappablePoint>(
  start: LatLng,
  ordered: T[],
  end: LatLng | null,
): number[] {
  if (ordered.length === 0) {
    return end ? [haversineMeters(start, end)] : [];
  }
  const legs: number[] = [haversineMeters(start, ordered[0])];
  for (let i = 1; i < ordered.length; i++) {
    legs.push(haversineMeters(ordered[i - 1], ordered[i]));
  }
  if (end) legs.push(haversineMeters(ordered[ordered.length - 1], end));
  return legs;
}

/**
 * Plan a delivery-style tour: start → visit every point → optional end.
 *
 * Strategy: greedy nearest-neighbor seed, polished with 2-opt until no swap
 * improves. NN alone is fast but produces classic "doubling back" failures on
 * elongated geographies (e.g. depot in Dearborn, stops down I-71 to Cincinnati
 * and a few outliers near Toledo); 2-opt fixes those by removing crossed
 * edges. Worst case ~3× NN runtime; for n ≤ 100 it's sub-millisecond.
 *
 * `end` semantics:
 *   - omitted → round-trip back to `start`
 *   - `null`  → open path, no return leg
 *   - point   → custom end (different end depot)
 */
export function optimizeRoute<T extends MappablePoint>(
  start: LatLng,
  points: T[],
  end?: LatLng | null,
): OrderedRoute<T> {
  const resolvedEnd: LatLng | null = end === undefined ? start : end;

  if (points.length === 0) {
    const legs = resolvedEnd ? [haversineMeters(start, resolvedEnd)] : [];
    return { ordered: [], legs, totalMeters: legs.reduce((s, x) => s + x, 0) };
  }

  const seeded = greedyNearestNeighbor(start, points);
  const polished = twoOptImprove(start, seeded, resolvedEnd);
  const legs = buildLegs(start, polished, resolvedEnd);
  return { ordered: polished, legs, totalMeters: legs.reduce((s, x) => s + x, 0) };
}

// Internal — used only by formatMiles below. Keep here so external callers
// import the pre-formatted string instead of doing their own conversion.
function metersToMiles(m: number): number {
  return m / 1609.344;
}

export function formatMiles(m: number): string {
  return `${metersToMiles(m).toFixed(1)} mi`;
}

/** Human-friendly drive duration: "12m", "1h 23m", "2h" for round numbers. */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const totalMin = Math.round(seconds / 60);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
